/**
 * People: the submissions that came from one human, grouped. As TanStack
 * Query hooks, beside crm.ts and activities.ts.
 *
 * A merge, an unlink or an undo changes the person's page, the people list,
 * the suggestions inbox, the people counts on the reports, the "+N more"
 * badges on the submissions list and the person's timeline, so every write
 * invalidates all of them.
 *
 * None of this is for buyer, supplier or otherwise scoped logins: the server
 * answers them 403, and every caller passes `enabled` from
 * useCrmPermissions().seesPeople so the request is never made.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from './client';
import type { components, paths } from './schema';

type S = components['schemas'];
export type PersonSummary = S['PersonSummary'];
export type PersonDetail = S['PersonDetail'];
export type PersonSubmission = S['PersonSubmission'];
export type PersonBrief = S['PersonBrief'];
export type PersonStream = S['PersonStream'];
export type PersonLinkView = S['PersonLinkView'];
export type PeopleSource = S['PeopleSource'];
export type IdentitySuggestion = S['IdentitySuggestion'];
export type IdentitySettings = S['IdentitySettings'];
export type IdentitySettingsBody = S['IdentitySettingsBody'];
export type PeopleCounts = S['PeopleCountsResponse'];

export type PeopleListParams = NonNullable<paths['/api/v1/people']['get']['parameters']['query']>;
export type PeopleCountsParams = NonNullable<paths['/api/v1/people/counts']['get']['parameters']['query']>;
export type SuggestionListParams = NonNullable<paths['/api/v1/identity/suggestions']['get']['parameters']['query']>;

/** Linking modes, as the server spells them. */
export type LinkMode = 'off' | 'suggest' | 'auto';

/** Drop empty values, so a cleared filter is not sent as `?q=`. */
function clean<T extends Record<string, unknown>>(params: T): T {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== '' && v !== undefined && v !== null),
  ) as T;
}

export function usePeople(params: PeopleListParams, enabled = true) {
  const query = clean(params);
  return useQuery({
    queryKey: ['people', query],
    queryFn: async () => unwrap(await api.GET('/api/v1/people', { params: { query } }), 'PeopleListResponse'),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export type PeopleSearchBody = S['PeopleSearchBody'];

/**
 * People over a leads search: the records page's people view, with the same
 * filter body as its rows view. A person is listed when any
 * of their submissions matches; their counts are over all of them.
 */
export function usePeopleSearch(body: PeopleSearchBody, enabled = true) {
  const query = clean(body as Record<string, unknown>) as PeopleSearchBody;
  return useQuery({
    queryKey: ['people', 'search', query],
    queryFn: async () =>
      unwrap(await api.POST('/api/v1/people/search', { body: query }), 'PeopleListResponse'),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function usePerson(id: string | null, enabled = true) {
  return useQuery({
    queryKey: ['person', id],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/people/{id}', { params: { path: { id: id! } } }), 'PersonDetail'),
    enabled: enabled && !!id,
  });
}

export function usePeopleCounts(params: PeopleCountsParams, enabled = true) {
  const query = clean(params);
  return useQuery({
    queryKey: ['people-counts', query],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/people/counts', { params: { query } }), 'PeopleCountsResponse'),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
    enabled,
    // While a merge has not reached the rollups the figures are exact but
    // slow; look again shortly so the mark goes away by itself.
    refetchInterval: (q) => (q.state.data?.updating ? 15 * 1000 : false),
  });
}

export function useSuggestions(params: SuggestionListParams, enabled = true) {
  const query = clean(params);
  return useQuery({
    queryKey: ['identity-suggestions', query],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/identity/suggestions', { params: { query } }), 'SuggestionListResponse'),
    staleTime: 15 * 1000,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useIdentitySettings(enabled = true) {
  return useQuery({
    queryKey: ['identity-settings'],
    queryFn: async () => unwrap(await api.GET('/api/v1/settings/identity', {}), 'IdentitySettings'),
    staleTime: 60 * 1000,
    enabled,
  });
}

/** Everything a merge, unlink, undo or decision can change on screen. */
function useInvalidatePeople() {
  const qc = useQueryClient();
  return () => {
    for (const key of ['people', 'person', 'people-counts', 'identity-suggestions', 'identity-settings', 'timeline', 'leads']) {
      qc.invalidateQueries({ queryKey: [key] });
    }
  };
}

export function useSaveIdentitySettings() {
  const invalidate = useInvalidatePeople();
  return useMutation({
    mutationFn: async (body: IdentitySettingsBody) =>
      unwrap(await api.PUT('/api/v1/settings/identity', { body }), 'IdentitySettings'),
    onSuccess: invalidate,
  });
}

/** Fold `otherId` into `id`. `id` is kept. */
export function useMergePeople() {
  const invalidate = useInvalidatePeople();
  return useMutation({
    mutationFn: async ({ id, otherId }: { id: string; otherId: string }) =>
      unwrap(
        await api.POST('/api/v1/people/{id}/merge', { params: { path: { id } }, body: { other_id: otherId } }),
        'PersonLinkResponse',
      ),
    onSuccess: invalidate,
  });
}

/** Take one submission out of its person, into a person of its own. */
export function useUnlinkSubmission() {
  const invalidate = useInvalidatePeople();
  return useMutation({
    mutationFn: async (submissionId: string) =>
      unwrap(
        await api.POST('/api/v1/submissions/{id}/unlink', { params: { path: { id: submissionId } } }),
        'PersonLinkResponse',
      ),
    onSuccess: invalidate,
  });
}

/** Undo a merge or an unlink. */
export function useUndoLink() {
  const invalidate = useInvalidatePeople();
  return useMutation({
    mutationFn: async (linkId: string) =>
      unwrap(
        await api.POST('/api/v1/people/links/{id}/undo', { params: { path: { id: linkId } } }),
        'PersonLinkResponse',
      ),
    onSuccess: invalidate,
  });
}

/** Accept (merge, the older person kept) or dismiss suggestions, in bulk. */
export function useDecideSuggestions() {
  const invalidate = useInvalidatePeople();
  return useMutation({
    mutationFn: async ({ ids, action }: { ids: string[]; action: 'accept' | 'dismiss' }) =>
      unwrap(await api.POST('/api/v1/identity/suggestions', { body: { ids, action } }), 'SuggestionDecisionResponse'),
    onSuccess: invalidate,
  });
}
